import { useEffect, useRef } from 'react';
import { UseKeyboardShortcutsProps } from './types';
import { useKeyCombinationEvent } from '../useKeyCombinationEvent';
import {
  COMPANY_SEARCH_FOCUS_CLICK_TIMEOUT,
  EXPAND_COMPANIES_MENU_TIMEOUT,
  SEARCH_SHORTCUT_CONFIG,
} from './constants';

export const useKeyboardShortcuts = ({
  setOpenSidebar,
}: UseKeyboardShortcutsProps) => {
  // Store action functions in refs to avoid stale closures
  const setOpenSidebarRef = useRef(setOpenSidebar);

  // Update refs when dependencies change
  useEffect(() => {
    setOpenSidebarRef.current = setOpenSidebar;
  }, [setOpenSidebar]);

  // Keyboard shortcuts with simple callbacks
  const handleSearchShortcut = useKeyCombinationEvent(
    SEARCH_SHORTCUT_CONFIG,
    event => {
      event.preventDefault();
      setOpenSidebarRef.current?.(true);
      setTimeout(() => {
        const sidebarMenuCompanies = document.querySelector('#sidebar-menu-companies') as HTMLButtonElement;
        if (sidebarMenuCompanies) {
          sidebarMenuCompanies.click();
          setTimeout(() => {
            const searchInput = document.querySelector('#filter-company') as HTMLInputElement;
            if (searchInput) {
              searchInput.focus();
              searchInput.click();
            }
          }, COMPANY_SEARCH_FOCUS_CLICK_TIMEOUT);
        }
      }, EXPAND_COMPANIES_MENU_TIMEOUT);
    }
  );

  // Store the event handler in a ref to prevent memory leaks
  const handleKeyDownRef = useRef<(event: KeyboardEvent) => void>();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Execute keyboard shortcuts - callbacks handle the actions internally
      handleSearchShortcut(event);
    };

    // Store the handler in ref for cleanup
    handleKeyDownRef.current = handleKeyDown;

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      if (handleKeyDownRef.current) {
        document.removeEventListener('keydown', handleKeyDownRef.current);
      }
    };
  }, [
    handleSearchShortcut
  ]);
};
